import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import '../App.css';

// --- Spinner Personalizado ---
const CustomSpinner = ({ size = 25, color = '#000' }) => (
  <div style={{
    width: size,
    height: size,
    border: `3px solid rgba(0,0,0,0.1)`,
    borderLeftColor: color,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }} />
);

function Edition() {
  const { organization, number } = useParams();
  const [groupedBattles, setGroupedBattles] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap');
    @keyframes spin { to { transform: rotate(360deg); } }
    
    .editions-title { font-weight: 900; font-size: 1.75rem; color: #1a1a1a; text-transform: uppercase; letter-spacing: -1px; border-left: 6px solid #007bff; padding-left: 15px; line-height: 1; margin-bottom: 20px; }
    
    /* --- Scoreboard Responsivo --- */
    .scoreboard-container {
      display: flex;
      justify-content: space-between; 
      align-items: center;
      margin-bottom: 1rem;
      width: 100%;
    }
    
    .team-side {
      flex: 1; 
      display: flex;
      flex-direction: row;
      align-items: center;
      min-width: 0;
    }

    .team-left { justify-content: flex-end; padding-right: 10px; }
    .team-right { justify-content: flex-start; padding-left: 10px; }
    
    .score-box {
      flex: 0 0 auto; 
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 60px;
    }

    .battle-img-responsive {
      width: 55px; 
      height: 55px;
      object-fit: cover;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .score-text {
      font-weight: bold;
      font-size: 1.5rem;
      color: #6c757d;
      white-space: nowrap;
    }

    @media (max-width: 576px) {
      .team-side { flex-direction: column; justify-content: center; }
      .team-left { align-items: flex-end; }
      .team-right { align-items: flex-start; }
      .battle-img-responsive { width: 40px !important; height: 40px !important; }
      .score-text { font-size: 1.2rem; }
    }
  `;

  const expectedPhases = [
    'FINAL',
    'SEMI_FINAL - GROUP_A',
    'SEMI_FINAL - GROUP_B',
    'PHASE_2 - GROUP_A',
    'PHASE_2 - GROUP_B',
    'PHASE_1 - GROUP_A',
    'PHASE_1 - GROUP_B',
  ];

  const phaseNameMap = (phase) => {
    let newPhase = phase;
    newPhase = newPhase.replace('PHASE_2', 'SEGUNDA FASE');
    newPhase = newPhase.replace('PHASE_1', 'PRIMEIRA FASE');
    newPhase = newPhase.replace('GROUP_A', 'LADO A');
    newPhase = newPhase.replace('GROUP_B', 'LADO B');
    return newPhase;
  };

  const phaseOrder = ['FINAL', 'SEMI_FINAL', 'PHASE_2', 'PHASE_1'];

  const orderedGroups = Object.keys(groupedBattles).sort((a, b) => {
    const getBase = (str) => str.split(' ')[0];
    return phaseOrder.indexOf(getBase(a)) - phaseOrder.indexOf(getBase(b));
  });

  const toggleCollapse = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const emptyStructure = expectedPhases.reduce((acc, phase) => {
      acc[phase] = [];
      return acc;
    }, {});
    setGroupedBattles(emptyStructure);
  }, []);

  useEffect(() => {
    const fetchBattleData = async () => {
      try {
        const response = await axios.get(
          `https://scoreboard-j6oz.onrender.com/editions/organization/${organization}/number/${number}/detail`
        );
        const battles = response.data.battle || [];
        const grouped = battles.reduce((acc, battle) => {
          const key = battle.phase === battle.side ? battle.phase : `${battle.phase} - ${battle.side}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(battle);
          return acc;
        }, {});
        const finalGrouped = expectedPhases.reduce((acc, phase) => {
          acc[phase] = grouped[phase] || [];
          return acc;
        }, {});
        setGroupedBattles(finalGrouped);
      } catch (error) {
        console.error('Erro ao buscar batalhas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBattleData();
  }, [organization, number]);

  const mcNameStyle = {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: '500',
    color: '#333',
    textTransform: 'capitalize',
    fontSize: '1.1rem',
    letterSpacing: '0.3px'
  };

  return (
    <div className="App">
      <style>{styles}</style>
      
      <nav className="navbar navbar-dark bg-primary">
        <div className="container">
          <button className="navbar-toggler" type="button" onClick={toggleMenu}>
            <span className="navbar-toggler-icon"></span>
          </button>
          <a className="navbar-brand" href="#"><b>Rhymes Score</b></a>
          <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`}>
            <ul className="navbar-nav ml-auto">
              <li className="nav-item"><Link className="nav-link" to="/">Home</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/topbigfour">Top 5 - Big Four</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/rankhype">Ranking do Hype</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/about">Sobre Nós</Link></li>
            </ul>
          </div>
        </div>
      </nav>

      <div className="main-content py-5">
        <div className="container">
          <h2 className="editions-title">Batalhas</h2>
          <div id="accordion">
            {orderedGroups.map((group, index) => (
              <div className="card mb-3 border-primary shadow" key={index}>
                <div className="card-header bg-primary text-white" id={`heading-${index}`}>
                  <h5 className="mb-0">
                    <button
                      className="btn btn-link text-white"
                      onClick={() => toggleCollapse(index)}
                      aria-expanded={activeIndex === index}
                      aria-controls={`collapse-${index}`}
                      style={{ textDecoration: 'none', fontWeight: 'bold', width: '100%', textAlign: 'left' }}
                    >
                      {phaseNameMap(group)}
                    </button>
                  </h5>
                </div>

                <div id={`collapse-${index}`} className={`collapse ${activeIndex === index ? 'show' : ''}`}>
                  <div className="card-body">
                    {loading ? (
                      <LoadingPhrase />
                    ) : groupedBattles[group].length === 0 ? (
                      <p className="text-muted text-center">Nenhuma batalha nesta fase ainda.</p>
                    ) : (
                      groupedBattles[group].map((battle, battleIndex) => (
                        <div className="card mb-4 shadow-sm border-0" key={battleIndex}>
                          <div className="card-body">
                            
                            {/* FOTOS + PLACAR */}
                            <div className="scoreboard-container">
                              <div className="team-side team-left">
                                {battle.winner?.mcs?.length > 0 ? (
                                  battle.winner.mcs.map((mc, mcIndex) => (
                                    <img
                                      key={mcIndex}
                                      src={mc.photo}
                                      alt={mc.nickName}
                                      className="battle-img-responsive m-1 shadow-sm"
                                      style={{ border: '2px solid #28a745', padding: '1px' }}
                                    />
                                  ))
                                ) : null}
                              </div>

                              <div className="score-box">
                                <h4 className="mb-0 score-text">
                                  {battle.winnerScore} <span style={{fontSize: '0.7em', color: '#ccc'}}>x</span> {battle.competitorScore}
                                </h4>
                                {battle.linkVideo && (
                                  <a href={battle.linkVideo} target="_blank" rel="noopener noreferrer" className="mt-1" title="Assistir Batalha">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                                      <polygon points="10 8 16 12 10 16 10 8" fill="#dc2626"></polygon>
                                    </svg>
                                  </a>
                                )}
                              </div>

                              <div className="team-side team-right">
                                {battle.competitor?.mcs?.length > 0 ? (
                                  battle.competitor.mcs.map((mc, mcIndex) => (
                                    <img
                                      key={mcIndex}
                                      src={mc.photo}
                                      alt={mc.nickName}
                                      className="battle-img-responsive m-1 shadow-sm"
                                    />
                                  ))
                                ) : null}
                              </div>
                            </div>

                            {/* NOMES DOS MCs - AJUSTE: Removido 'flex: 1' para centralizar no meio */}
                            <div className="d-flex align-items-center justify-content-center w-100 mt-2">
                              
                              {/* Lado Vencedor - Sem flex:1 */}
                              <div className="text-center">
                                {battle.winner?.mcs?.length > 0
                                  ? battle.winner.mcs.map((mc, i) => (
                                      <span key={i} style={mcNameStyle}>
                                        {mc.nickName} {i < battle.winner.mcs.length - 1 ? ' & ' : ''}
                                      </span>
                                    ))
                                  : <span className="text-muted small">Sem MC</span>}
                              </div>

                              {/* Traço Separador */}
                              <span className="mx-3 text-muted" style={{ fontWeight: '300', fontSize: '1.2rem', flexShrink: 0 }}>—</span>

                              {/* Lado Competidor - Sem flex:1 */}
                              <div className="text-center">
                                {battle.competitor?.mcs?.length > 0
                                  ? battle.competitor.mcs.map((mc, i) => (
                                      <span key={i} style={mcNameStyle}>
                                        {mc.nickName} {i < battle.competitor.mcs.length - 1 ? ' & ' : ''}
                                      </span>
                                    ))
                                  : <span className="text-muted small">Sem MC</span>}
                              </div>

                            </div>

                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="bg-primary text-white text-center py-3">
        <p className="mb-0">© 2024 Rhymes Score. Todos os direitos reservados</p>
      </footer>
    </div>
  );
}

const LoadingPhrase = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [index, setIndex] = useState(0);
  const [words, setWords] = useState([]);

  useEffect(() => {
    const phrases = [
      'Barras e Barras e Barras, cê pensa que o meu freestyle é uma diss, quando eu parei você era o Murica, mas quando eu voltei o putu de Paris',
      'Souja cê não entende, é diferente eu fui o primeiro a fazer rima tipo Souja que foi o primeiro a ter uma marca de videogame',
      'To tranquilo e calmo, chego na norte amasso Glaubin, cê vai virar alvo, confundi meu mano logo é o Youngui, roubei a bike eu sou o malvo',
      'Pow! Pow! combina com o barulho do tiro quando eu engato a minha AK, Pow! Pow!',
      'O trio exaltado mas não tem patente, você foi humilhado, entendeu o que nois sente ?',
    ];
    const chosen = phrases[Math.floor(Math.random() * phrases.length)];
    const wordList = chosen.split(' ');
    setWords(wordList);
    setDisplayedText('');
    setIndex(0);
  }, []);

  useEffect(() => {
    if (index < words.length) {
      const interval = setInterval(() => {
        setDisplayedText((prev) => `${prev} ${words[index]}`);
        setIndex((prev) => prev + 1);
      }, 400);
      return () => clearInterval(interval);
    }
  }, [index, words]);

  return (
    <div className="text-center d-flex align-items-center justify-content-center flex-column">
      <CustomSpinner color="#000" size={25} />
      <p className="mt-2 text-muted" style={{ fontStyle: 'italic' }}>{displayedText.trim()}</p>
    </div>
  );
};

export default Edition;